import LoginFailure from '@/components/auth/LoginFailure';
import { Logger } from '@/utils/logger';
import type { apiV1Contract } from '@montugroup/circuit-api-contracts';
import { useAuthCheck, zodEmailValidate } from '@montugroup/circuit-shared';
import { CodeInputDialog } from '@montugroup/circuit-shared/components/common/CodeInputDialog';
import { useLogin } from '@montugroup/circuit-shared/hooks/auth/useLogin';
import type { MontuError } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { Box, Link, Stack, TextField, Typography } from '@montugroup/design-system';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

const logger = new Logger('PharmacistLogInForm');

type SignInFormData = {
  email: string;
  password: string;
};

function SignInForm() {
  useAuthCheck({ defaultAuthedUserRoute: '/orders' });
  const navigate = useNavigate();
  const location = useLocation();
  const { register, handleSubmit, formState } = useForm<SignInFormData>({
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const { errors } = formState;
  const [submissionError, setSubmissionError] = useState<string | MontuError<typeof apiV1Contract.user.login>>('');
  const { login, isPending, mfaController } = useLogin();

  const onSubmit: SubmitHandler<SignInFormData> = async (formData) => {
    setSubmissionError('');
    const userFrom = location?.state?.from?.pathname || '';
    const userFromState = location?.state?.from?.state || {};
    let user;

    try {
      user = await login({ email: formData.email.toLowerCase().trim(), password: formData.password });
    } catch (err) {
      logger.info('AuthService.login failed: ', err);
      setSubmissionError(err as string);
    }

    if (!user || !('user' in user)) {
      return;
    }

    if (userFrom && !userFrom.includes('logout') && !userFrom.includes('login')) {
      logger.info('navigating to: ', userFrom);
      navigate(
        {
          pathname: userFrom,
        },
        {
          replace: true,
          state: userFromState,
        },
      );
    } else {
      logger.info('navigating to /orders');
      navigate('/orders', {
        replace: true,
      });
    }
  };

  return (
    <Box>
      <LoginFailure error={submissionError} showSupportText />
      <Typography
        sx={(theme) => ({ color: theme.palette.primary.main })}
        variant="h5"
        align="left"
        data-testid="sign-in-form"
      >
        Pharmacist login
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          label="Email"
          fullWidth
          margin="normal"
          variant="outlined"
          error={Boolean(errors.email?.message)}
          helperText={errors.email?.message}
          {...register('email', {
            required: 'You must enter an email address',
            validate: zodEmailValidate,
          })}
          autoComplete="email"
          sx={{
            marginBottom: 0,
          }}
        />
        <TextField
          label="Password"
          type="password"
          fullWidth
          margin="normal"
          variant="outlined"
          error={Boolean(errors.password?.message)}
          helperText={errors.password?.message}
          autoComplete="off"
          {...register('password', {
            required: 'You must enter a password',
          })}
        />
        <LoadingButton
          type="submit"
          id="btnSubmitthree"
          variant="contained"
          color="secondary"
          fullWidth
          loading={isPending}
          onClick={handleSubmit(onSubmit)}
          data-testid="login-button"
        >
          Login
        </LoadingButton>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ alignSelf: 'stretch', mt: 4 }}>
          <Link component={RouterLink} to={`/forgot-password`} color="primary">
            Forgot password?
          </Link>
        </Stack>
      </form>
      <CodeInputDialog controller={mfaController} />
    </Box>
  );
}

export default SignInForm;
